// Geometry for Drugs' Molecule section (features/molecule.ts): a receptor's Cα trace as tubes coloured by secondary
// structure, its ligand as balls and sticks, and the pose that stands the structure upright beside the brain. Pure
// functions over pipelines/pharma-structures' compact files (schema brain-atlas.pharma-structures.v1): coordinates come
// in ångströms and go out in millimetres at the section's scale, about the structure's Cα centre; the pose's rotation
// and translation place it in the scene, so the engine lights the normals as sent.

export type Vec3 = [number, number, number];
export interface Geometry {
  positions: Float32Array;
  normals: Float32Array;
  indices: Uint32Array;
}
export type TubeClass = 'helix' | 'strand' | 'coil' | 'other';
export interface ChainLike {
  ca: Vec3[];
  ss: { kind: 'helix' | 'strand'; from: number; to: number }[];
  gaps: number[];
  target: boolean;
}
export interface LigandLike {
  atoms: { x: number; y: number; z: number }[];
  bonds: [number, number][];
}

/** Vertices around a tube and spline samples per residue. */
export const SIDES = 8;
export const SUBDIVISIONS = 4;
/** The tubes' radii, Å: fat for helices and strands, as a cartoon's tube mode draws them, thin for coil, and thinner for
 *  chains that aren't the target (fusions, G proteins, antibody fragments), which draw as one dim tube. */
export const TUBE_RADIUS_A: Record<TubeClass, number> = { helix: 1.5, strand: 1.2, coil: 0.55, other: 0.6 };
export const ATOM_RADIUS_A = 0.45;
export const BOND_RADIUS_A = 0.2;

const sub = (a: Vec3, b: Vec3): Vec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const dot = (a: Vec3, b: Vec3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross = (a: Vec3, b: Vec3): Vec3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const times = (a: Vec3, k: number): Vec3 => [a[0] * k, a[1] * k, a[2] * k];
const unit = (a: Vec3): Vec3 => {
  const l = Math.hypot(a[0], a[1], a[2]) || 1;
  return [a[0] / l, a[1] / l, a[2] / l];
};
const empty = (): Geometry => ({ positions: new Float32Array(0), normals: new Float32Array(0), indices: new Uint32Array(0) });

/** A uniform Catmull-Rom spline through the points: `per` samples a segment and the last point, (n − 1) × per + 1 samples
 *  for n ≥ 2 points, the first and last point repeated for the end tangents. Each sample names the residue it is nearest
 *  to. One point gives itself; none gives nothing. */
export function spline(points: readonly Vec3[], per = SUBDIVISIONS): { points: Vec3[]; residue: number[] } {
  const n = points.length;
  if (n === 0) return { points: [], residue: [] };
  if (n === 1) return { points: [[...points[0]!] as Vec3], residue: [0] };
  const out: Vec3[] = [];
  const residue: number[] = [];
  const at = (i: number) => points[Math.min(n - 1, Math.max(0, i))]!;
  for (let i = 0; i < n - 1; i++) {
    const p0 = at(i - 1);
    const p1 = at(i);
    const p2 = at(i + 1);
    const p3 = at(i + 2);
    for (let k = 0; k < per; k++) {
      const t = k / per;
      const t2 = t * t;
      const t3 = t2 * t;
      const p: Vec3 = [0, 0, 0];
      for (let c = 0; c < 3; c++) {
        p[c] = 0.5 * (2 * p1[c]! + (-p0[c]! + p2[c]!) * t + (2 * p0[c]! - 5 * p1[c]! + 4 * p2[c]! - p3[c]!) * t2 + (-p0[c]! + 3 * p1[c]! - 3 * p2[c]! + p3[c]!) * t3);
      }
      out.push(p);
      residue.push(t < 0.5 ? i : i + 1);
    }
  }
  out.push([...points[n - 1]!] as Vec3);
  residue.push(n - 1);
  return { points: out, residue };
}

/** A tube of radius `r` along the samples: SIDES vertices a ring, rotation-minimising frames so it doesn't twist,
 *  analytic normals, triangles wound to face out. Fewer than two samples give an empty geometry. */
export function tube(samples: readonly Vec3[], r: number, sides = SIDES): Geometry {
  const n = samples.length;
  if (n < 2) return empty();
  const positions = new Float32Array(n * sides * 3);
  const normals = new Float32Array(n * sides * 3);
  const indices = new Uint32Array((n - 1) * sides * 6);
  let N: Vec3 = [0, 0, 1];
  for (let j = 0; j < n; j++) {
    const T = unit(sub(samples[Math.min(n - 1, j + 1)]!, samples[Math.max(0, j - 1)]!));
    if (j === 0) {
      const a: Vec3 = Math.abs(T[2]) < 0.9 ? [0, 0, 1] : [1, 0, 0];
      N = unit(sub(a, times(T, dot(T, a))));
    } else {
      const projected = sub(N, times(T, dot(T, N)));
      if (Math.hypot(projected[0], projected[1], projected[2]) > 1e-6) N = unit(projected);
    }
    const B = cross(T, N);
    const p = samples[j]!;
    for (let s = 0; s < sides; s++) {
      const th = (2 * Math.PI * s) / sides;
      const c = Math.cos(th);
      const si = Math.sin(th);
      const nx = c * N[0] + si * B[0];
      const ny = c * N[1] + si * B[1];
      const nz = c * N[2] + si * B[2];
      const v = (j * sides + s) * 3;
      positions[v] = p[0] + r * nx;
      positions[v + 1] = p[1] + r * ny;
      positions[v + 2] = p[2] + r * nz;
      normals[v] = nx;
      normals[v + 1] = ny;
      normals[v + 2] = nz;
    }
  }
  let q = 0;
  for (let j = 0; j < n - 1; j++) {
    for (let s = 0; s < sides; s++) {
      const a = j * sides + s;
      const b = j * sides + ((s + 1) % sides);
      const c = a + sides;
      const d = b + sides;
      indices[q++] = a;
      indices[q++] = b;
      indices[q++] = c;
      indices[q++] = b;
      indices[q++] = d;
      indices[q++] = c;
    }
  }
  return { positions, normals, indices };
}

/** A sphere at `c` of radius `r`: `lat` bands by `lon` slices, wound to face out. */
export function sphere(c: Vec3, r: number, lat = 6, lon = 10): Geometry {
  const positions: number[] = [];
  const normals: number[] = [];
  const indices: number[] = [];
  for (let i = 0; i <= lat; i++) {
    const phi = (Math.PI * i) / lat;
    const sp = Math.sin(phi);
    const cp = Math.cos(phi);
    for (let j = 0; j <= lon; j++) {
      const th = (2 * Math.PI * j) / lon;
      const nx = sp * Math.cos(th);
      const ny = cp;
      const nz = sp * Math.sin(th);
      positions.push(c[0] + r * nx, c[1] + r * ny, c[2] + r * nz);
      normals.push(nx, ny, nz);
    }
  }
  for (let i = 0; i < lat; i++) {
    for (let j = 0; j < lon; j++) {
      const a = i * (lon + 1) + j;
      const b = a + lon + 1;
      indices.push(a, a + 1, b, a + 1, b + 1, b);
    }
  }
  return { positions: Float32Array.from(positions), normals: Float32Array.from(normals), indices: Uint32Array.from(indices) };
}

/** Geometries as one, the indices offset. */
export function merge(parts: readonly Geometry[]): Geometry {
  if (parts.length === 1) return parts[0]!;
  let nv = 0;
  let ni = 0;
  for (const p of parts) {
    nv += p.positions.length;
    ni += p.indices.length;
  }
  const positions = new Float32Array(nv);
  const normals = new Float32Array(nv);
  const indices = new Uint32Array(ni);
  let ov = 0;
  let oi = 0;
  for (const p of parts) {
    positions.set(p.positions, ov);
    normals.set(p.normals, ov);
    const base = ov / 3;
    for (let k = 0; k < p.indices.length; k++) indices[oi + k] = p.indices[k]! + base;
    ov += p.positions.length;
    oi += p.indices.length;
  }
  return { positions, normals, indices };
}

/** The class of each residue: helix or strand where a range names it, coil elsewhere; 'other' over the `dim` ranges (a crystal
 *  construct's fusion inside the target's own chain: BRIL, T4 lysozyme), whatever their structure. */
export function residueClasses(chain: Pick<ChainLike, 'ca' | 'ss'>, dim: readonly { from: number; to: number }[] = []): TubeClass[] {
  const out: TubeClass[] = new Array<TubeClass>(chain.ca.length).fill('coil');
  for (const s of chain.ss) for (let i = Math.max(0, s.from); i <= Math.min(chain.ca.length - 1, s.to); i++) out[i] = s.kind;
  for (const d of dim) for (let i = Math.max(0, d.from); i <= Math.min(chain.ca.length - 1, d.to); i++) out[i] = 'other';
  return out;
}

/** The chain's residues as runs the ribbon may bridge: split at its gaps (a gap index `i` parts `i − 1` from `i`). */
export function runsOf(count: number, gaps: readonly number[]): [number, number][] {
  const cuts = [...new Set(gaps.filter((g) => g > 0 && g < count))].sort((a, b) => a - b);
  const out: [number, number][] = [];
  let start = 0;
  for (const g of cuts) {
    out.push([start, g - 1]);
    start = g;
  }
  if (count > 0) out.push([start, count - 1]);
  return out;
}

/** One chain's tubes by class: each run splined, each sample classed by its residue, pieces of one class reaching one
 *  sample into the next so they meet; a chain that isn't the target is one thin 'other' tube regardless of structure.
 *  `toMm` takes a Cα position (Å) to the mesh's millimetres; `dim` names the target chain's residues to draw dim (fusions).
 *  Runs of one residue draw nothing. */
export function chainTubes(chain: ChainLike, toMm: (p: Vec3) => Vec3, scaleMmPerA: number, dim: readonly { from: number; to: number }[] = []): Partial<Record<TubeClass, Geometry[]>> {
  const out: Partial<Record<TubeClass, Geometry[]>> = {};
  const push = (k: TubeClass, g: Geometry) => (out[k] ??= []).push(g);
  const classes = residueClasses(chain, dim);
  for (const [a, b] of runsOf(chain.ca.length, chain.gaps)) {
    if (b - a < 1) continue;
    const run = chain.ca.slice(a, b + 1).map(toMm);
    const s = spline(run);
    if (!chain.target) {
      push('other', tube(s.points, TUBE_RADIUS_A.other * scaleMmPerA));
      continue;
    }
    const cls = s.residue.map((r) => classes[a + r]!);
    let from = 0;
    while (from < cls.length) {
      let to = from;
      while (to + 1 < cls.length && cls[to + 1] === cls[from]) to++;
      const k = cls[from]!;
      const piece = s.points.slice(Math.max(0, from - 1), Math.min(cls.length, to + 2));
      push(k, tube(piece, TUBE_RADIUS_A[k] * scaleMmPerA));
      from = to + 1;
    }
  }
  return out;
}

/** A ligand as balls and sticks: a sphere an atom, a tube a bond. */
export function ligandGeometry(lig: LigandLike, toMm: (p: Vec3) => Vec3, scaleMmPerA: number): Geometry {
  const atoms = lig.atoms.map((a) => toMm([a.x, a.y, a.z]));
  const parts: Geometry[] = atoms.map((p) => sphere(p, ATOM_RADIUS_A * scaleMmPerA));
  for (const [i, j] of lig.bonds) {
    const a = atoms[i];
    const b = atoms[j];
    if (a && b) parts.push(tube([a, b], BOND_RADIUS_A * scaleMmPerA));
  }
  return parts.length ? merge(parts) : empty();
}

/** The points' mean and orthonormal principal axes, longest first, as a right-handed triple (Jacobi on the 3 × 3
 *  covariance). Fewer than two points give the identity axes about the point. */
export function principalAxes(points: readonly Vec3[]): { centre: Vec3; axes: [Vec3, Vec3, Vec3] } {
  const n = points.length;
  const centre: Vec3 = [0, 0, 0];
  for (const p of points) for (let c = 0; c < 3; c++) centre[c] = centre[c]! + p[c]! / n;
  if (n < 2) return { centre: n ? centre : [0, 0, 0], axes: [[1, 0, 0], [0, 1, 0], [0, 0, 1]] };
  const a = [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
  ];
  for (const p of points) {
    const d = sub(p, centre);
    for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) a[i]![j]! += (d[i]! * d[j]!) / n;
  }
  const v = [
    [1, 0, 0],
    [0, 1, 0],
    [0, 0, 1],
  ];
  for (let sweep = 0; sweep < 60; sweep++) {
    let off = 0;
    for (let i = 0; i < 3; i++) for (let j = i + 1; j < 3; j++) off += a[i]![j]! * a[i]![j]!;
    if (off < 1e-18) break;
    for (let p = 0; p < 3; p++) {
      for (let q = p + 1; q < 3; q++) {
        const apq = a[p]![q]!;
        if (Math.abs(apq) < 1e-15) continue;
        const theta = (a[q]![q]! - a[p]![p]!) / (2 * apq);
        const t = Math.sign(theta || 1) / (Math.abs(theta) + Math.sqrt(theta * theta + 1));
        const c = 1 / Math.sqrt(t * t + 1);
        const s = t * c;
        for (let k = 0; k < 3; k++) {
          const akp = a[k]![p]!;
          const akq = a[k]![q]!;
          a[k]![p] = c * akp - s * akq;
          a[k]![q] = s * akp + c * akq;
        }
        for (let k = 0; k < 3; k++) {
          const apk = a[p]![k]!;
          const aqk = a[q]![k]!;
          a[p]![k] = c * apk - s * aqk;
          a[q]![k] = s * apk + c * aqk;
        }
        for (let k = 0; k < 3; k++) {
          const vkp = v[k]![p]!;
          const vkq = v[k]![q]!;
          v[k]![p] = c * vkp - s * vkq;
          v[k]![q] = s * vkp + c * vkq;
        }
      }
    }
  }
  const order = [0, 1, 2].sort((i, j) => a[j]![j]! - a[i]![i]!);
  const axes = order.map((k) => unit([v[0]![k]!, v[1]![k]!, v[2]![k]!])) as [Vec3, Vec3, Vec3];
  if (dot(cross(axes[0], axes[1]), axes[2]) < 0) axes[2] = times(axes[2], -1);
  return { centre, axes };
}

/** The column-major model → world pose for a mesh in millimetres about its centre: the longest axis up (world z), the
 *  second along x (the width a front view sees), the third along y, then the translation to `place`. A rotation (det +1)
 *  and a translation, no scale, as the viewport's `mesh` and `setPose` take. */
export function uprightPose(axes: [Vec3, Vec3, Vec3], place: Vec3): Float32Array {
  const [e1, e2, e3] = axes;
  const m = new Float32Array(16);
  for (let c = 0; c < 3; c++) {
    m[c * 4] = e2[c]!;
    m[c * 4 + 1] = e3[c]!;
    m[c * 4 + 2] = e1[c]!;
  }
  m[12] = place[0];
  m[13] = place[1];
  m[14] = place[2];
  m[15] = 1;
  return m;
}

/** A point through a column-major 4 × 4. */
export function transformPoint(m: Float32Array, p: Vec3): Vec3 {
  return [m[0]! * p[0] + m[4]! * p[1] + m[8]! * p[2] + m[12]!, m[1]! * p[0] + m[5]! * p[1] + m[9]! * p[2] + m[13]!, m[2]! * p[0] + m[6]! * p[1] + m[10]! * p[2] + m[14]!];
}

/** The axis-aligned bounds of the points. */
export function bounds(points: readonly Vec3[]): { min: Vec3; max: Vec3; centre: Vec3; size: Vec3 } {
  const min: Vec3 = [Infinity, Infinity, Infinity];
  const max: Vec3 = [-Infinity, -Infinity, -Infinity];
  for (const p of points) {
    for (let c = 0; c < 3; c++) {
      if (p[c]! < min[c]!) min[c] = p[c]!;
      if (p[c]! > max[c]!) max[c] = p[c]!;
    }
  }
  if (!points.length) return { min: [0, 0, 0], max: [0, 0, 0], centre: [0, 0, 0], size: [0, 0, 0] };
  return { min, max, centre: [(min[0] + max[0]) / 2, (min[1] + max[1]) / 2, (min[2] + max[2]) / 2], size: sub(max, min) };
}
